import { useContext } from 'react';
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from 'cmdk';
import { Check } from 'lucide-react';

import { useDebounce } from '@hooks/use-debounce';

import { EQLFilterValuesContext } from './context';
import { EQLFilterFieldConfig, UseEQLFilterValues } from './types';

import css from './styles.module.css';

const SEARCH_DEBOUNCE_MS = 250;

const noValues: UseEQLFilterValues = () => ({
  values: [],
  isLoading: false,
  isError: false,
});

export interface EQLFilterValueListProps {
  field: EQLFilterFieldConfig;
  search: string;
  staticValues: string[];
  selected?: string | null;
  onSelect: (value: string) => void;
}

export function EQLFilterValueList({
  field,
  search,
  staticValues,
  selected,
  onSelect,
}: EQLFilterValueListProps) {
  const useValues = useContext(EQLFilterValuesContext);
  const isRemote = Boolean(field.fetchValues && useValues);
  const typed = search.trim();
  const debouncedTyped = useDebounce(typed, SEARCH_DEBOUNCE_MS);

  const useFieldValues = useValues ?? noValues;

  const firstPage = useFieldValues({
    field,
    search: '',
    enabled: isRemote,
  });

  const searchesRemotely = Boolean(
    isRemote && !firstPage.isError && firstPage.hasMore && debouncedTyped
  );
  const searchResults = useFieldValues({
    field,
    search: debouncedTyped,
    enabled: searchesRemotely,
  });

  const hasRemoteValues = isRemote && !firstPage.isError;
  function visibleOptions() {
    if (!hasRemoteValues) return staticValues;
    if (field.fetchValues === 'counts') {
      return staticValues.filter((value) => firstPage.counts?.[value]);
    }
    if (searchesRemotely && !searchResults.isLoading) {
      return searchResults.values;
    }
    const needle = typed.toLowerCase();
    return firstPage.values.filter((value) =>
      value.toLowerCase().includes(needle)
    );
  }

  const options = visibleOptions();
  const counts = searchesRemotely ? searchResults.counts : firstPage.counts;
  const isLoading = hasRemoteValues && firstPage.isLoading;

  const canUseTyped = typed !== '' && !options.includes(typed);

  return (
    <CommandList className={css.list}>
      {isLoading && <div className={css.empty}>Loading values...</div>}
      {!isLoading && !canUseTyped && (
        <CommandEmpty className={css.empty}>
          No values found in these logs
        </CommandEmpty>
      )}
      <CommandGroup>
        {canUseTyped ? (
          <CommandItem
            forceMount
            value={`use-typed-${typed}`}
            onSelect={() => onSelect(typed)}
            className={css.item}
          >
            <span className={css.label}>Use &ldquo;{typed}&rdquo;</span>
            {options.length === 0 && !isLoading ? (
              <span className={css.hint}>no match in these logs</span>
            ) : null}
          </CommandItem>
        ) : null}
        {options.map((option) => (
          <CommandItem
            key={option}
            value={option}
            onSelect={() => onSelect(option)}
            className={css.item}
          >
            <span className={css.label}>{option}</span>
            {hasRemoteValues && counts?.[option] !== undefined ? (
              <span className={css.hint}>
                {counts[option].toLocaleString()}
              </span>
            ) : null}
            {selected === option ? <Check className={css.check} /> : null}
          </CommandItem>
        ))}
      </CommandGroup>
    </CommandList>
  );
}
